<script setup lang="ts">
import Typography from '@components/Typography.vue';
import CheckboxGroupFilter from '@pages/search/components/filters/CheckableTagGroup.vue';
import { trans } from '@tools/translation.ts';

interface NetworkLine {
  unique_code: string;
  name: string;
  exponent?: string;
}

interface Network {
  type: string;
  name: string;
  lines: NetworkLine[];
}

interface TransportsProps {
  selected?: string[];
  networks?: Network[];
}

const {
  selected = [],
  // Use database data from https://bureaux-a-partager.atlassian.net/browse/MATCH-10658
  networks = [
    {
      type: 'ratp-metro',
      lines: [
        { unique_code: 'ratp-metro-1', name: '1' },
        { unique_code: 'ratp-metro-2', name: '2' },
        { unique_code: 'ratp-metro-3', name: '3' },
        { unique_code: 'ratp-metro-3 BIS', name: '3', exponent: 'Bis' },
        { unique_code: 'ratp-metro-4', name: '4' },
        { unique_code: 'ratp-metro-5', name: '5' },
        { unique_code: 'ratp-metro-6', name: '6' },
        { unique_code: 'ratp-metro-7', name: '7' },
        { unique_code: 'ratp-metro-7 BIS', name: '7', exponent: 'Bis' },
        { unique_code: 'ratp-metro-8', name: '8' },
        { unique_code: 'ratp-metro-9', name: '9' },
        { unique_code: 'ratp-metro-10', name: '10' },
        { unique_code: 'ratp-metro-11', name: '11' },
        { unique_code: 'ratp-metro-12', name: '12' },
        { unique_code: 'ratp-metro-13', name: '13' },
        { unique_code: 'ratp-metro-14', name: '14' },
      ],
    },
    {
      type: 'ratp-rer',
      lines: [
        { unique_code: 'ratp-rer-A', name: 'A' },
        { unique_code: 'ratp-rer-B', name: 'B' },
        { unique_code: 'ratp-rer-C', name: 'C' },
        { unique_code: 'ratp-rer-D', name: 'D' },
        { unique_code: 'ratp-rer-E', name: 'E' },
      ],
    },
    {
      type: 'ratp-tram',
      lines: [
        { unique_code: 'ratp-tram-1', name: '1' },
        { unique_code: 'ratp-tram-2', name: '2' },
        { unique_code: 'ratp-tram-3a', name: '3a' },
        { unique_code: 'ratp-tram-3b', name: '3b' },
        { unique_code: 'ratp-tram-4', name: '4' },
        { unique_code: 'ratp-tram-5', name: '5' },
        { unique_code: 'ratp-tram-6', name: '6' },
        { unique_code: 'ratp-tram-7', name: '7' },
        { unique_code: 'ratp-tram-8', name: '8' },
      ],
    },
    {
      type: 'ratp-transilien',
      lines: [
        { unique_code: 'ratp-transilien-H', name: 'H' },
        { unique_code: 'ratp-transilien-J', name: 'J' },
        { unique_code: 'ratp-transilien-K', name: 'K' },
        { unique_code: 'ratp-transilien-L', name: 'L' },
        { unique_code: 'ratp-transilien-N', name: 'N' },
        { unique_code: 'ratp-transilien-P', name: 'P' },
        { unique_code: 'ratp-transilien-R', name: 'R' },
        { unique_code: 'ratp-transilien-U', name: 'U' },
      ],
    },
  ],
} = defineProps<TransportsProps>();

const emit = defineEmits<{(e: 'update:selected', value: string[]): void}>();

const formatLinesForGroup = (lines: NetworkLine[]) => lines.map((line) => ({
  code: line.unique_code,
  label: `${trans('global.line')} ${line.name}`,
  exponent: line.exponent ?? '',
}));

</script>

<template>
  <div>
    <div
      v-for="network in networks"
      :key="network.type"
    >
      <Typography
        variant="sub-heading"
        color="gray-darker"
        class="py-3"
      >
        {{ trans(`global.${network.type}`) }}
      </Typography>

      <CheckboxGroupFilter
        :options="formatLinesForGroup(network.lines)"
        :selected="selected"
        class="pb-4"
        @update:selected="emit('update:selected', $event)"
      />
    </div>
  </div>
</template>
